import { DestroyRef, DOCUMENT, Injectable, NgZone, inject, signal } from '@angular/core';
import { USER_SETTINGS_SERVICE, UserSettings, isEnhancedUiEnabled } from '@infinitform/platform-core';
import { REDUCED_MOTION_QUERY } from '../accessibility/reduced-motion';
import { PointerGlowTracker } from './pointer-glow-tracker';

/**
 * `<html data-enhanced-ui="on|off">`: every Enhanced UI style keys off this.
 * Present only while a screen that shows the effects (an editor screen, the
 * cabinet shell, the signed-out pages) is attached.
 */
export const ENHANCED_UI_ATTRIBUTE = 'data-enhanced-ui';
/** Mirror of the setting in this origin's localStorage, read before the settings document arrives. */
export const ENHANCED_UI_STORAGE_KEY = 'enhancedUi';

const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';
const FORCED_COLORS_QUERY = '(forced-colors: active)';

/**
 * The "Enhanced UI" appearance setting (`graphics.enhancedUi`, on by default).
 * Screens that show the effects call `attach()` on init and `detach()` on
 * destroy (or use `attachEnhancedUi()`), and nothing shows outside that window
 * of time: the editor screens, the cabinet shell of the cloud and desktop
 * apps, and the full-page signed-out screens.
 *
 * While attached it marks `<html>` and runs the pointer-glow tracker when it
 * can have a visible effect. Persisting is the caller's job: the editor and
 * the cabinet settings page save it with the rest of the graphics settings.
 */
@Injectable({ providedIn: 'root' })
export class EnhancedUiService {
  private readonly doc = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly userSettings = inject(USER_SETTINGS_SERVICE, { optional: true });

  private readonly enabledSignal = signal<boolean>(this.loadLocal());
  readonly enabled = this.enabledSignal.asReadonly();

  private readonly tracker = new PointerGlowTracker(this.doc);
  private readonly queries: MediaQueryList[] = [];
  private readonly onMediaChange = (): void => this.syncTracker();
  private readonly onStorage = (event: StorageEvent): void => {
    if (event.key !== ENHANCED_UI_STORAGE_KEY || event.newValue === null) return;
    const next = event.newValue !== 'off';
    if (next === this.enabledSignal()) return;
    this.enabledSignal.set(next);
    this.renderAttribute();
    this.syncTracker();
  };
  private destroyed = false;
  /** Screens currently attached; overlapping during a route change is fine. */
  private attachCount = 0;

  constructor() {
    this.zone.runOutsideAngular(() => {
      const view = this.doc.defaultView;
      if (view && typeof view.matchMedia === 'function') {
        for (const query of [FINE_POINTER_QUERY, REDUCED_MOTION_QUERY, FORCED_COLORS_QUERY]) {
          const list = view.matchMedia(query);
          list.addEventListener('change', this.onMediaChange);
          this.queries.push(list);
        }
      }
      view?.addEventListener('storage', this.onStorage);
    });
    this.syncTracker();
    inject(DestroyRef).onDestroy(() => this.destroy());
  }

  /** A screen that shows the effects is mounted: they may run. Pair with `detach()`. */
  attach(): void {
    this.attachCount++;
    this.renderAttribute();
    this.syncTracker();
  }

  /** The screen is gone: with no other screen attached, the attribute and the listeners go with it. */
  detach(): void {
    this.attachCount = Math.max(0, this.attachCount - 1);
    this.renderAttribute();
    this.syncTracker();
  }

  /** Whether any screen is attached. */
  isAttached(): boolean {
    return this.attachCount > 0;
  }

  /** A user-made change: apply now and remember it in this browser. */
  setEnabled(enabled: boolean): void {
    this.enabledSignal.set(enabled);
    this.storeLocal(enabled);
    this.renderAttribute();
    this.syncTracker();
  }

  /** Adopt the value from a settings document (absent means on). */
  hydrate(settings: Pick<UserSettings, 'graphics'> | null | undefined): void {
    const enabled = isEnhancedUiEnabled(settings);
    this.storeLocal(enabled);
    if (enabled === this.enabledSignal()) return;
    this.enabledSignal.set(enabled);
    this.renderAttribute();
    this.syncTracker();
  }

  /**
   * Read the user's settings (the shared, cached read) and adopt the value.
   * Call once the user is known -- in the cloud apps that means after sign-in.
   * A failed read keeps the locally stored value.
   */
  syncFromUserSettings(): void {
    this.userSettings?.get().subscribe({
      next: (settings) => this.hydrate(settings),
      error: () => undefined,
    });
  }

  /** Whether the pointer listeners are registered right now. */
  isPointerTracking(): boolean {
    return this.tracker.isListening;
  }

  /**
   * The tracker runs only while the glow can show: a screen attached, the
   * setting on, a mouse-like pointer, no reduced-motion or forced-colours
   * preference. Otherwise no listeners exist at all.
   */
  private syncTracker(): void {
    const shouldTrack =
      !this.destroyed && this.isAttached() && this.enabledSignal() && this.pointerEffectsAllowed();
    if (shouldTrack === this.tracker.isListening) return;
    if (shouldTrack) {
      this.zone.runOutsideAngular(() => this.tracker.start());
    } else {
      this.tracker.stop();
    }
  }

  private pointerEffectsAllowed(): boolean {
    if (this.queries.length === 0) return false;
    const [finePointer, reducedMotion, forcedColors] = this.queries;
    return finePointer.matches && !reducedMotion.matches && !forcedColors.matches;
  }

  private renderAttribute(): void {
    const html = this.doc.documentElement;
    if (this.isAttached() && !this.destroyed) {
      html.setAttribute(ENHANCED_UI_ATTRIBUTE, this.enabledSignal() ? 'on' : 'off');
    } else {
      html.removeAttribute(ENHANCED_UI_ATTRIBUTE);
    }
  }

  private loadLocal(): boolean {
    try {
      return localStorage.getItem(ENHANCED_UI_STORAGE_KEY) !== 'off';
    } catch {
      return true;
    }
  }

  private storeLocal(enabled: boolean): void {
    try {
      localStorage.setItem(ENHANCED_UI_STORAGE_KEY, enabled ? 'on' : 'off');
    } catch {
      // Storage can be unavailable (private mode, quota); the attribute still applies.
    }
  }

  private destroy(): void {
    this.destroyed = true;
    this.renderAttribute();
    this.tracker.stop();
    for (const list of this.queries) list.removeEventListener('change', this.onMediaChange);
    this.queries.length = 0;
    this.doc.defaultView?.removeEventListener('storage', this.onStorage);
  }
}
